/**
 * Validate email address format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if email is valid
 */
export const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {boolean} True if password meets requirements
 */
export const validatePassword = (password) => {
    // Minimum 8 characters as per API documentation
    return password && password.length >= 8
}

/**
 * Validate username format
 * @param {string} username - Username to validate
 * @returns {boolean} True if username meets requirements
 */
export const validateUsername = (username) => {
    // 3-50 characters, alphanumeric and underscores only
    const usernameRegex = /^[a-zA-Z0-9_]{3,50}$/
    return usernameRegex.test(username)
}

/**
 * Validate phone number format (basic validation)
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if phone number appears valid
 */
export const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return true // Optional field

    // Basic phone number validation - allows international format
    const phoneRegex = /^[+]?[1-9][\d\s\-()]{7,15}$/
    return phoneRegex.test(phoneNumber.replace(/\s/g, ""))
}

/**
 * Validate required field
 * @param {string} value - Value to validate
 * @returns {boolean} True if value is not empty
 */
export const validateRequired = (value) => {
    return (
        value !== null &&
        value !== undefined &&
        value.toString().trim().length > 0
    )
}

/**
 * Validate form data object
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result with errors object
 */
export const validateRegistrationForm = (formData) => {
    const errors = {}

    // Required fields
    if (!validateRequired(formData.email)) {
        errors.email = "Email is required"
    } else if (!validateEmail(formData.email)) {
        errors.email = "Please enter a valid email address"
    }

    if (!validateRequired(formData.username)) {
        errors.username = "Username is required"
    } else if (!validateUsername(formData.username)) {
        errors.username =
            "Username must be 3-50 characters and contain only letters, numbers, and underscores"
    }

    if (!validateRequired(formData.password)) {
        errors.password = "Password is required"
    } else if (!validatePassword(formData.password)) {
        errors.password = "Password must be at least 8 characters long"
    }

    if (!validateRequired(formData.confirmPassword)) {
        errors.confirmPassword = "Please confirm your password"
    } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Passwords do not match"
    }

    // Optional fields validation
    if (formData.phoneNumber && !validatePhoneNumber(formData.phoneNumber)) {
        errors.phoneNumber = "Please enter a valid phone number"
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    }
}
